import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  LicensePriceQuote,
  LicensePriceQuoteRequest,
  LicenseRate,
  LicenseRateRequest,
  PublicLicenseRatesRequest,
} from '@models';
import { map, shareReplay, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class LicenseRatesService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly functions = inject(Functions);

  public all$ = this.tenantContext.tenantId$.pipe(
    switchMap(() => this.firestore.listDocuments<LicenseRate>(this.col)),
    shareReplay(1),
  );

  private get col() {
    return this.tenantContext.collections.LICENSE_RATES;
  }

  get(id: string) {
    return this.all$.pipe(map((rates) => rates.find((r) => r.id === id)!));
  }

  create(rate: Omit<LicenseRate, 'id'>) {
    return this.firestore.create<LicenseRate>(this.col, rate);
  }

  update(id: string, rate: Partial<LicenseRate>) {
    return this.firestore.update<LicenseRate>(this.col, { id, ...rate });
  }

  deleteForever(id: string) {
    return this.firestore.deleteForever(this.col, id);
  }

  findLicenseGeneralPrice(data: LicenseRateRequest) {
    const callable = httpsCallable<
      LicenseRateRequest & { tenantId: string },
      number
    >(this.functions, 'findLicensePrice');
    return callable({ ...data, tenantId: this.tenantContext.tenantId });
  }

  getPublicLicenseRates() {
    const callable = httpsCallable<PublicLicenseRatesRequest, LicenseRate[]>(
      this.functions,
      'getPublicLicenseRates',
    );
    return callable({ tenantId: this.tenantContext.tenantId });
  }

  /**
   * Cotiza el importe DEFINITIVO de una licencia (callable
   * `quoteLicensePrice`).
   *
   * El importe devuelto lo resuelve el servidor con la misma función que usa
   * `payLicense` para cobrar, así que es el que se cargará realmente: el precio
   * del catálogo público puede diferir cuando el tenant tiene tarifas por
   * categoría. El callable es público (el flujo de `/federate` cotiza también
   * sin sesión); con sesión, el servidor ignora este `tenantId` y usa el de los
   * claims.
   *
   * `available: false` NO es un error: significa que no hay tarifa aplicable
   * a la categoría/género del usuario, y el llamante debe distinguirlo de un
   * fallo técnico (la promesa rechaza).
   */
  quoteLicensePrice(payload: Omit<LicensePriceQuoteRequest, 'tenantId'>) {
    const callable = httpsCallable<LicensePriceQuoteRequest, LicensePriceQuote>(
      this.functions,
      'quoteLicensePrice',
    );
    return callable({ ...payload, tenantId: this.tenantContext.tenantId });
  }
}
